import {
  t,
  translate,
} from "components/CentralDataMangement/translation/Translation";
import Button from "components/common/atoms/Button";
import SignatureField from "components/common/atoms/SignatureField";
import FileUpload from "components/common/molecules/FileUpload";
import { ADD_SIGNATURE } from "routes/ApiEndpoints";
import { ApiCall } from "components/common/services/ApiServices";
import React, { useEffect, useState } from "react";
import {
  fetchAuthedBlobUrl,
  downloadAuthedFile,
} from "components/common/services/apiUtils";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { CANDIDATE_MICROSERVICE, CENTRAL_DATA_MANAGEMENT_MICROSERVICE, IDENTITY_MANAGER_MICROSERVICE } from "Constants";
import ModalPopup from "components/common/atoms/ModalPopup";
import { LabelWithInputField } from "components/common/molecules/LabelWithInputField";
import { ValidationRules } from "components/common/utlis/TypeAnnotations";
import {
  validateEmail,
  validateForm,
  validatePassword,
  validateRequired,
} from "components/common/services/ValidationService";
import CustomNotify from "components/common/atoms/CustomNotify";
import { addUserDetails, selectAuth } from "features/auth/AuthSlice";
import { useDispatch, useSelector } from "react-redux";
import secureLocalStorage from "react-secure-storage";

interface Preview {
  iframeUrl: string;
  candidateSign: boolean;
  signAsCandidatePopup: boolean;
  signaturePadPopup: boolean;
  candidateVerify: boolean;
  email: string;
  password: string;
  candidateId: number | null;
  canUserId: number | null;
  uploadModalOpen: boolean;
  uploadedFileId: number | null;
  uploadedFileName: string;
  uploadedFilePath: string;
  uploadedExtension: string;
}

const CandidateConsentForm = () => {
  const { candidateId } = useParams<{
    candidateId: string;
  }>();
  const location = useLocation();
  const userAuth = useSelector(selectAuth);
  const candidate = userAuth.role === "CANDIDATE";
  const navigate = useNavigate();
  const lang_key = secureLocalStorage.getItem("site_lang") ?? "en";
  const dispatch = useDispatch();

  const initialState = {
    // showSign=0: hide the candidate signature in the preview until the candidate
    // is verified (prevents exposing the signature pre-authentication).
    iframeUrl: `${process.env.REACT_APP_BACKEND_SERVICE}/api/get-candidate-consent-details?candidateId=${candidateId}&lang=${lang_key}&showSign=0`,
    candidateSign: true,
    signAsCandidatePopup: false,
    signaturePadPopup: false,
    candidateVerify: candidate ?? false,
    email: "",
    password: "",
    candidateId: null,
    canUserId: null,
    uploadModalOpen: false,
    uploadedFileId: null,
    uploadedFileName: "",
    uploadedFilePath: "",
    uploadedExtension: "",
  };
  const [state, setState] = useState<Preview>(initialState);

  // The consent preview endpoint moved behind auth:sanctum (TO-38233); an <iframe>
  // can't send a Bearer, so we fetch the HTML with the token and render it from a
  // blob object URL. state.iframeUrl holds the logical backend URL; this resolves
  // it to an authenticated object URL and revokes the previous one on change.
  const [iframeBlobUrl, setIframeBlobUrl] = useState<string>("");
  useEffect(() => {
    let active = true;
    let createdUrl: string | null = null;
    (async () => {
      if (!state.iframeUrl) {
        return;
      }
      const path = state.iframeUrl.replace(
        process.env.REACT_APP_BACKEND_SERVICE ?? "",
        ""
      );
      const objectUrl = await fetchAuthedBlobUrl(path);
      if (active && objectUrl) {
        createdUrl = objectUrl;
        setIframeBlobUrl(objectUrl);
      }
    })();
    return () => {
      active = false;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [state.iframeUrl]);

  const initialErrors: { [key: string]: string } = {};
  const [errors, setErrors] = useState<{ [key: string]: string }>(
    initialErrors
  );
  // Inline "skip screening?" confirmation for the Matching shortcut, mirroring
  // the post-create success popup behaviour.
  const [showSkipScreening, setShowSkipScreening] = useState(false);
  // After any consent action (send / sign / upload) succeeds, show the
  // Screening / Matching / Do-it-later options in a popup.
  const [showOptionsPopup, setShowOptionsPopup] = useState(false);

  // Skip screening and advance the candidate straight to matching. Same todo
  // update the ScreeningAndMatchingModalPopup performs.
  const handleMatching = async () => {
    const response = await ApiCall.service(
      "updateTodo",
      "POST",
      { candidateId: candidateId, stage: "Matching", submitType: "skip" },
      false,
      CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );
    if (response?.status === 200) {
      navigate(`/matching-list/candidate/${candidateId}`);
    } else {
      CustomNotify({ type: "error", message: t("Something went wrong") });
    }
  };
  const handleModalClose = (type: string) => {
    if (type === "candidateSign") {
      setState((prevData) => ({
        ...prevData,
        signAsCandidatePopup: false,
      }));
    } else if (type === "signaturePad") {
      setState((prevData) => ({
        ...prevData,
        signaturePadPopup: false,
      }));
    } else {
      setState((prevData) => ({
        ...prevData,
        candidateSign: true,
      }));
    }
  };

  const validation = (
    name: string,
    value: string | boolean | number,
    isSingleFieldValidation: boolean = false
  ) => {
    const validationRules: ValidationRules = {
      email: [validateRequired, validateEmail],
      password: [validateRequired, validatePassword],
    };

    const validationErrors = validateForm(
      { ...state, [name]: value },
      validationRules,
      isSingleFieldValidation ? name : undefined
    );

    if (isSingleFieldValidation && Object.keys(errors).length > 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationErrors[name],
      }));
    } else {
      setErrors(validationErrors);
    }

    if (Object.keys(validationErrors).length > 0) {
      return false;
    }

    return true;
  };
  const checkCandidateSign = async () => {
    const url = `${"checkCandidateSign"}/${candidateId}`;
    const response = await ApiCall.getService(
      url,
      "GET",
      CANDIDATE_MICROSERVICE,
      true
    );

    if (response?.status === 200) {
      const data = response?.data;
      const signExists = data?.signExists === true;

      // No authenticate (email/password) step: the candidate is in front of the
      // recruiter. If a signature already exists, mark verified and reveal it so
      // they can Submit. Otherwise open the signature pad to draw one.
      setState((prevData) => ({
        ...prevData,
        candidateSign: true,
        signAsCandidatePopup: false,
        signaturePadPopup: !signExists,
        candidateVerify: signExists ? true : prevData.candidateVerify,
        email: data?.userMail ?? "",
        canUserId: data?.userId ?? null,
        iframeUrl: `${process.env.REACT_APP_BACKEND_SERVICE}/api/get-candidate-consent-details?candidateId=${candidateId}&lang=${lang_key}${signExists ? "&approved=1&showSign=1" : "&showSign=0"}`,
      }));
    } else {
      CustomNotify({
        type: "error",
        message: t("Some error occured")
      });
    }
  };

  // Persist a freshly drawn signature against the candidate's user id, mirroring
  // configurationAndSettings/signature/AddSignature.tsx. No authenticate step:
  // on success, mark verified and reveal the signature so they can Submit.
  const saveSign = async (signData: string) => {
    if (!state.canUserId) {
      CustomNotify({ type: "error", message: t("Some error occured") });
      return;
    }
    const response = await ApiCall.service(
      ADD_SIGNATURE,
      "POST",
      { sign: signData, entityId: state.canUserId },
      true,
      CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );
    if (response?.status === 200) {
      setState((prevData) => ({
        ...prevData,
        signaturePadPopup: false,
        signAsCandidatePopup: false,
        candidateVerify: true,
        iframeUrl: `${process.env.REACT_APP_BACKEND_SERVICE}/api/get-candidate-consent-details?candidateId=${candidateId}&lang=${lang_key}&approved=1&showSign=1`,
      }));
    } else {
      CustomNotify({ type: "error", message: t("Some error occured") });
    }
  };

  // ---- Upload a physically-signed consent document (PDF/JPG/PNG) ----
  const handleUpload = () => {
    setState((prevData) => ({ ...prevData, uploadModalOpen: true }));
  };

  // FileUpload molecule returns { response: [fileId, fileName, filePath] }.
  const documentUploadResponse = (responseObj: any) => {
    const response = responseObj?.["response"];
    if (!response) return;
    const fileNameWithExtension = response[1] ?? "";
    const lastDotIndex = fileNameWithExtension.lastIndexOf(".");
    const extension =
      lastDotIndex !== -1 ? fileNameWithExtension.substring(lastDotIndex + 1) : "";
    setState((prevData) => ({
      ...prevData,
      uploadedFileId: response[0],
      uploadedFileName: fileNameWithExtension,
      uploadedFilePath: response[2],
      uploadedExtension: extension,
    }));
  };

  const documentDeleteResponse = () => {
    setState((prevData) => ({
      ...prevData,
      uploadedFileId: null,
      uploadedFileName: "",
      uploadedFilePath: "",
      uploadedExtension: "",
    }));
  };

  // The FileUpload molecule calls this for BOTH success (error: "") and real
  // validation failures (format/size). Only surface an actual error message.
  const documentUploadErrors = (errorInfo: any) => {
    if (errorInfo?.error) {
      CustomNotify({ type: "error", message: t(errorInfo.error) });
    }
  };

  // Link the uploaded signed file to the candidate, reusing the registered
  // authenticateCandidate endpoint (submitType: "upload").
  const saveUploadedConsent = async () => {
    if (!state.uploadedFileId) {
      CustomNotify({
        type: "warning",
        message: t("Please upload the signed document"),
      });
      return;
    }
    const response = await ApiCall.service(
      "authenticateCandidate",
      "POST",
      {
        submitType: "upload",
        candidateId: candidateId,
        fileId: state.uploadedFileId,
        fileName: state.uploadedFileName,
        fileExtension: state.uploadedExtension,
        lang: lang_key,
      },
      true,
      CANDIDATE_MICROSERVICE
    );
    if (response?.status === 200) {
      CustomNotify({ type: "success", message: t(response?.msg) });
      setState((prevData) => ({ ...prevData, uploadModalOpen: false }));
      dispatch(addUserDetails({ consentStatus: 1 }));
      if (userAuth.role === "CANDIDATE") {
        navigate(`/dashboard`);
      } else {
        // Recruiter: offer Screening / Matching / Do-it-later in a popup.
        setShowOptionsPopup(true);
      }
    } else {
      CustomNotify({
        type: "error",
        message: t(response?.msg ?? response?.message ?? "Some error occured"),
      });
    }
  };

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setState((prevData) => {
      // Reveal the signature in the preview only once the candidate is verified
      // (logged-in candidate, or recruiter after the candidate authenticates).
      const showSign = prevData.candidateVerify ? 1 : 0;
      return {
        ...prevData,
        [name]: value,
        iframeUrl: `${process.env.REACT_APP_BACKEND_SERVICE}/api/get-candidate-consent-details?candidateId=${candidateId}&lang=${lang_key}&showSign=${showSign}`,
      };
    });
    validation(name, value, true);
  };

  const authenticateCandidate = async (event: React.FormEvent) => {
    event.preventDefault();
    const { name, value } = event.target as HTMLInputElement;
    const formData = {
      email: state.email,
      // TO-38127 (f_fesec_d4e29ffeed): plaintext over HTTPS (backend decryptOrPlain
      // accepts both); JSON.stringify kept so the hashed value is unchanged.
      password: JSON.stringify(state.password),
      candidateId: candidateId,
      canUserId: state.canUserId,
      submitType: "accept",
      lang: lang_key,
    };
    if (validation(name, value)) {
      const response = await ApiCall.service(
        "authenticateCandidate",
        "POST",
        formData,
        true,
        CANDIDATE_MICROSERVICE
      );
      if (response?.status === 200) {
        setState((prevData) => ({
          ...prevData,
          candidateVerify: true,
          signAsCandidatePopup: false,
          // Candidate is now verified - reveal the signature in the preview.
          iframeUrl: `${process.env.REACT_APP_BACKEND_SERVICE}/api/get-candidate-consent-details?candidateId=${candidateId}&lang=${lang_key}&approved=1&showSign=1`,
        }));
        setErrors({
          email: "",
        });
      } else if (response?.accountPending) {
        // Account not activated yet: resend the same activation/confirm-password
        // email registration uses, so the candidate can set a password then sign.
        await ApiCall.service(
          "resend-confirm-password",
          "POST",
          { email: state.email, appLangKey: lang_key },
          true,
          IDENTITY_MANAGER_MICROSERVICE
        );
        CustomNotify({
          type: "success",
          message: t(
            "Activation email has been sent. Please set the password from the email, then sign."
          ),
        });
        setErrors({ email: response?.msg });
      } else {
        CustomNotify({
          type: "error",
          message: t("Some error occured")
        });
        setErrors({
          email: response?.msg,
        });
      }
    }
  };

  const handleSubmit = async (toCandidate: boolean) => {
    const formData = {
      approved: toCandidate ? false : true,
      sendToCandidate: toCandidate,
      candidateId: candidateId,
      recruiterId: !candidate ? userAuth.userId : null,
      canUserId: candidate ? userAuth.userId : state.canUserId,
      email: state.email,
      verify: 1,
      submitType: "accept",
      lang: lang_key,
    };
    const response = await ApiCall.service(
      "authenticateCandidate",
      "POST",
      formData,
      true,
      CANDIDATE_MICROSERVICE
    );

    if (response?.status === 200) {
      CustomNotify({
        type: "success",
        message: t(response?.msg),
      });
      dispatch(addUserDetails({ consentStatus: 1 }));
      if (userAuth.role === "CANDIDATE") {
        navigate(`/dashboard`);
      } else {
        // Recruiter: offer Screening / Matching / Do-it-later in a popup.
        setShowOptionsPopup(true);
      }
    } else {
      const data = response?.data;
      if (data) {
        setState((prevData) => ({
          ...prevData,
          candidateSign: false,
        }));
      }
      !data && CustomNotify({ type: "error", message: t(response?.msg) });
    }
  };

  return (
    <div>
      <iframe
        width="100%"
        src={iframeBlobUrl}
        title="Embedded Content"
        allowFullScreen
        className="iframeHeight"
      ></iframe>
      <div className="col-lg-12">
        <div className="row">
          <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <Button
              title={t("Back")}
              handleClick={() => navigate(-1)}
              className="back-btn text-decoration-underline shadow-none bg-transparent border-0"
            />
            <div className="d-flex flex-wrap gap-2">
              {state.candidateVerify === true && (
                <Button
                  type="submit"
                  title={t("Submit")}
                  handleClick={() => handleSubmit(false)}
                  className="form-button shadow-none"
                />
              )}

              {!candidate && state.candidateVerify === false && (
                <>
                  <Button
                    type="submit"
                    title={t("Send to candidate")}
                    handleClick={() => handleSubmit(true)}
                    className="form-button shadow-none"
                  />
                  <Button
                    type="submit"
                    title={t("Sign as candidate")}
                    handleClick={checkCandidateSign}
                    className="form-button shadow-none"
                  />
                  <Button
                    type="submit"
                    title={t("Upload signed document")}
                    handleClick={handleUpload}
                    className="form-button shadow-none"
                  />
                </>
              )}

              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  downloadAuthedFile(
                    `/api/pdf/${candidateId}/${userAuth.userId}/consent?lang=${lang_key}`,
                    `consent_${candidateId}.pdf`
                  );
                }}
                className="form-button shadow-none d-flex align-items-center justify-content-center"
              >
                {t("Download")}
              </a>
            </div>
          </div>
        </div>

      </div>
      <ModalPopup
        show={showOptionsPopup}
        onHide={() => navigate("/manage-candidates")}
        title={t("What would you like to do next?")}
        body={
          <div className="text-center">
            <Link to={`/screening/${candidateId}`} className="text-decoration-none">
              <span className="form-button d-flex align-items-center justify-content-center w-75 mx-auto mb-3">
                {t("Screening")}
              </span>
            </Link>
            {!showSkipScreening && (
              <button
                type="button"
                onClick={() => setShowSkipScreening(true)}
                className="form-button d-flex align-items-center justify-content-center w-75 mx-auto mb-3 border-0"
              >
                {t("Matching")}
              </button>
            )}
            {showSkipScreening && (
              <div className="mb-3">
                <div className="pb-3">
                  {t("Do you want to skip screening") + "?"}
                </div>
                <Button
                  title={t("Yes")}
                  handleClick={handleMatching}
                  className="form-button shadow-none marginRightPoint5"
                />
                <Button
                  title={t("No")}
                  handleClick={() => {
                    setShowSkipScreening(false);
                    navigate(`/screening/${candidateId}`);
                  }}
                  className="delete-btn shadow-none"
                />
              </div>
            )}
            <Link to={"/manage-candidates"} className="text-decoration-none">
              <span className="form-button d-flex align-items-center justify-content-center w-75 mx-auto">
                {t("Do it later")}
              </span>
            </Link>
          </div>
        }
        className="modal-sm"
      />
      <ModalPopup
        show={!state.candidateSign}
        onHide={() => handleModalClose("authenticated")}
        title={t("Signature")}
        body={
          candidate
            ? t("Please add the signature to continue further")
            : t(
              "Candidate not added the signature, Please add the signature to continue further"
            )
        }
        className="modal-lg"
      />
      <ModalPopup
        show={state.signaturePadPopup}
        onHide={() => handleModalClose("signaturePad")}
        title={t("Sign as candidate")}
        body={
          <SignatureField
            className={"signatureCanvas"}
            onSave={saveSign}
            width={"100%"}
          />
        }
        className="modal-lg"
      />
      <ModalPopup
        show={state.signAsCandidatePopup}
        onHide={() => handleModalClose("candidateSign")}
        title={t("Sign as candidate")}
        body={
          <form onSubmit={authenticateCandidate}>
            <div>
              <LabelWithInputField
                handleChange={changeHandler}
                label={t("Email")}
                type="text"
                isDisabled={true}
                id="email"
                name="email"
                value={state.email}
                error={errors.email}
              />
              <LabelWithInputField
                handleChange={changeHandler}
                isMandatory={true}
                label={t("Password")}
                type="password"
                id="password"
                name="password"
                value={state.password}
                error={errors.password}
              />
              <Button
                type="submit"
                title={t("Authenticate")}
                className="float-end form-button shadow-none marginRightPoint5"
              />
            </div>
          </form>
        }
        className="modal-lg"
      />
      <ModalPopup
        show={state.uploadModalOpen}
        onHide={() =>
          setState((prevData) => ({ ...prevData, uploadModalOpen: false }))
        }
        title={t("Upload signed document")}
        body={
          <div>
            <FileUpload
              id="signedConsent"
              name="signedConsent"
              label={t("Upload the physically signed document (PDF, JPG, PNG)")}
              isMandatory={true}
              edit={false}
              fileId={state.uploadedFileId}
              fileName={state.uploadedFileName}
              filePath={state.uploadedFilePath}
              multiple={false}
              uploadPath={"candidate_consent"}
              formats=".pdf,.jpg,.jpeg,.png"
              returnFileUploadErrors={documentUploadErrors}
              fileUploadResponse={documentUploadResponse}
              deleteResponse={documentDeleteResponse}
              maxFileSize={10 * 1024 * 1024}
              microserviceURL={`${process.env.REACT_APP_BACKEND_SERVICE}`}
            />
            <Button
              type="submit"
              title={t("Save")}
              handleClick={saveUploadedConsent}
              className="float-end form-button shadow-none marginRightPoint5 mt-3"
            />
          </div>
        }
        className="modal-lg"
      />
    </div>
  );
};

export default translate(CandidateConsentForm);
